import {
  ConeGeometry,
  CylinderGeometry,
  IcosahedronGeometry,
  Object3D,
  TorusGeometry,
  Vector3
} from 'three';
import { Ability } from './Ability.js';
import { settings } from '../config/settings.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { RateEmitter } from '../particles/ParticleEngine.js';
import { ConstructStyle } from '../materials/ConstructMaterial.js';
import { BurstMode } from '../effects/BurstSphere.js';
import { DecalType } from '../effects/GroundDecals.js';
import { getColor } from '../utils/color.js';
import { saturate, Easing } from '../utils/math.js';
import {
  createConstruct,
  setConstructInstance,
  commitConstructInstances,
  syncConstruct,
  syncParticlePalette,
  emitParticles,
  lifecycleFade,
  logicalImpactTime,
  safeTimelineDuration,
  disposeConstruct
} from './NovelSpellUtils.js';

const MAX_SEEDS = 12;
const THORNS_PER_SEED = 3;
const MAX_BRANCH_SEGMENTS = 1 + MAX_SEEDS * 2;
const TAU = Math.PI * 2;

const _a = new Vector3();
const _b = new Vector3();
const _elbow = new Vector3();
const _pos = new Vector3();
const _start = new Vector3();
const _target = new Vector3();
const _scale = new Vector3();
const _rot = new Vector3();
const _dir = new Vector3();
const _axis = new Vector3();
const _up = new Vector3(0, 1, 0);
const _segment = new Object3D();
const _emit = {};

/** Pose one preallocated cylinder instance between two arbitrary points. */
function setSegment(mesh, index, start, end, radius) {
  _axis.copy(end).sub(start);
  const length = Math.max(0.0001, _axis.length());
  _axis.multiplyScalar(1 / length);
  _segment.position.copy(start).add(end).multiplyScalar(0.5);
  _segment.quaternion.setFromUnitVectors(_up, _axis);
  _segment.scale.set(radius, length, radius);
  _segment.updateMatrix();
  mesh.setMatrixAt(index, _segment.matrix);
}

/**
 * BRAMBLE ORRERY — a growing organic sentry with radial seed volleys.
 *
 * A central stem unfolds into an armillary crown. Its pods orbit while the
 * structure blooms, then launch one by one toward deterministic points around
 * the promised zone. Every landing grows its own three-thorn cluster, making
 * the firing order and occupied radial positions readable without live targets.
 */
export class BrambleOrreryAbility extends Ability {
  constructor(context) {
    super('bramble', context);
  }

  createShaders() {
    this.branchGeometry = new CylinderGeometry(1, 1, 1, 7, 1, false);
    this.ringGeometry = new TorusGeometry(1, 0.035, 8, 56);
    this.podGeometry = new IcosahedronGeometry(1, 1);
    this.thornGeometry = new ConeGeometry(1, 1, 6, 1, false);
    this.branches = createConstruct(
      this.branchGeometry,
      ConstructStyle.BLADE,
      { count: MAX_BRANCH_SEGMENTS, normal: true }
    );
    this.rings = createConstruct(
      this.ringGeometry,
      ConstructStyle.ENERGY,
      { count: 2 }
    );
    this.pods = createConstruct(
      this.podGeometry,
      ConstructStyle.PRISM,
      { count: MAX_SEEDS }
    );
    this.thorns = createConstruct(
      this.thornGeometry,
      ConstructStyle.BLADE,
      { count: MAX_SEEDS * THORNS_PER_SEED, normal: true }
    );

    this.group.add(
      this.branches.mesh,
      this.rings.mesh,
      this.pods.mesh,
      this.thorns.mesh
    );

    this.centre = new Vector3();
    this.crownPosition = new Vector3();
    this.emitter = new RateEmitter();
    this.seed = 0;
    this.launchedMask = 0;
    this.landedMask = 0;
  }

  createParticles() {
    this.leaves = this.ctx.particles.get('bramble.leaves', {
      capacity: 3600,
      shape: ParticleShape.LEAF,
      additive: false,
      lit: true,
      curl: true,
      swirl: true,
      softFade: 0.48
    });
    this.leaves.uniforms.uGravity.value.set(0, -1.8, 0);
    this.leaves.uniforms.uDrag.value = 1.25;
    this.leaves.uniforms.uSwirl.value = 1.6;
    this.leaves.uniforms.uEndSize.value = 0.58;
    this.leaves.uniforms.uFadeOut.value = 0.46;
  }

  get impactDuration() {
    return settings.bramble.lifetime * settings.global.lifetime;
  }

  get fadeDuration() {
    return settings.bramble.fadeTime;
  }

  get instanceCount() {
    return this.branches.mesh.count + this.rings.mesh.count +
      this.pods.mesh.count + this.thorns.mesh.count;
  }

  onSpawn() {
    this.seed = Math.random() * TAU;
    this.launchedMask = 0;
    this.landedMask = 0;
    this.emitter.reset();
    commitConstructInstances(this.branches.mesh, 0);
    commitConstructInstances(this.rings.mesh, 0);
    commitConstructInstances(this.pods.mesh, 0);
    commitConstructInstances(this.thorns.mesh, 0);
  }

  onTravel(dt) {
    const c = settings.bramble;
    _pos.copy(this.position).setY(Math.max(0.32, c.thornHeight * 0.42));
    _rot.set(this.age * 1.7, this.age * c.flow * 2.6, this.age * 1.2);
    setConstructInstance(
      this.pods.mesh,
      0,
      _pos,
      Math.max(0.18, c.particleSize * 4.1),
      _rot
    );
    commitConstructInstances(this.pods.mesh, 1);
    syncConstruct(this.pods, c, {
      seed: this.seed,
      reveal: 1,
      fade: 1,
      pulse: 0.5 + 0.5 * Math.sin(this.age * 10)
    });

    syncParticlePalette(this.leaves, c);
    const count = this.emitter.tick(dt, c.particleRate * 0.24);
    if (count > 0) {
      emitParticles(this.leaves, _emit, {
        position: _pos,
        direction: _dir.copy(this.direction).multiplyScalar(-1).setY(0.25).normalize(),
        count,
        radius: 0.12,
        speed: 1.5,
        spread: 0.65,
        size: c.particleSize,
        life: c.particleLife
      });
    }
  }

  onImpact() {
    const c = settings.bramble;
    this.pointAt(1, this.centre).setY(0);
    this.crownPosition.copy(this.centre).setY(c.thornHeight);
    this.position.copy(this.crownPosition);
    commitConstructInstances(this.pods.mesh, 0);
    this.lightBoost = c.impactLight;

    this.ctx.bursts.spawn(BurstMode.EARTH, this.centre, {
      radius: 0.18,
      endRadius: c.innerRadius * 1.35,
      life: 0.58,
      intensity: 0.9,
      colorA: getColor(c.colorC),
      colorB: getColor(c.colorB),
      colorC: getColor(c.colorA)
    });
    this.ctx.decals.spawn(DecalType.CRACK, this.centre, {
      radius: c.innerRadius * 1.2,
      life: c.lifetime + c.fadeTime,
      intensity: 0.55,
      colorA: getColor(c.colorB),
      colorB: getColor(c.colorC)
    });
    this.ctx.shake.add(c.impactShake * settings.global.cameraShake, c.shakeDuration);
    this.ctx.flash.trigger(getColor(c.colorC), c.impactFlash);
  }

  /** Point on a cast-oriented ring; angle zero lies on the local side axis. */
  _ringPoint(out, radius, angle, height) {
    return out.copy(this.centre)
      .addScaledVector(this.side, Math.cos(angle) * radius)
      .addScaledVector(this.direction, Math.sin(angle) * radius)
      .setY(height);
  }

  _targetPoint(index, count, out) {
    const c = settings.bramble;
    const angle = this.seed + index * TAU / count + (index % 2 ? 0.2 : -0.2);
    const radiusJitter = 0.82 + 0.18 * (0.5 + 0.5 * Math.sin(index * 8.17 + this.seed));
    return this._ringPoint(out, c.orbitRadius * radiusJitter, angle, 0.04);
  }

  onFade(dt, t) {
    const c = settings.bramble;
    const fade = lifecycleFade(t);
    const time = logicalImpactTime(this.impactTime);
    const bloomEnd = safeTimelineDuration(c.bloomTime);
    const huntDuration = safeTimelineDuration(c.huntTime);
    const bloom = Easing.outBack(saturate(time / bloomEnd));
    const seedCount = Math.max(3, Math.min(MAX_SEEDS, Math.round(c.seeds)));
    const launchSpacing = huntDuration / seedCount;
    const flightDuration = safeTimelineDuration(launchSpacing * 1.42);
    const structure = bloom * fade;
    const trunkHeight = c.thornHeight * 1.65 * structure;
    const branchRadius = Math.max(0.045, c.particleSize * 0.78) * fade;

    // One trunk and two crooked segments per radial arm.
    _a.copy(this.centre).setY(0.02);
    _b.copy(this.centre).setY(Math.max(0.021, trunkHeight));
    setSegment(this.branches.mesh, 0, _a, _b, branchRadius * 1.5);
    let branchCount = 1;
    for (let i = 0; i < seedCount; i++) {
      const angle = this.seed + i * TAU / seedCount;
      _a.copy(this.centre).setY(trunkHeight * 0.53);
      this._ringPoint(
        _elbow,
        c.innerRadius * 0.38 * structure,
        angle - 0.14,
        trunkHeight * (0.72 + 0.06 * Math.sin(i * 2.4 + this.seed))
      );
      this._ringPoint(
        _b,
        c.innerRadius * 0.76 * structure,
        angle,
        trunkHeight * (0.58 + 0.1 * Math.sin(i * 1.7 + this.seed))
      );
      setSegment(this.branches.mesh, branchCount++, _a, _elbow, branchRadius);
      setSegment(this.branches.mesh, branchCount++, _elbow, _b, branchRadius * 0.82);
    }
    commitConstructInstances(this.branches.mesh, branchCount);
    syncConstruct(this.branches, c, {
      seed: this.seed,
      reveal: bloom,
      fade,
      pulse: 0.35 + 0.25 * Math.sin(this.age * 2.3)
    });

    this.crownPosition.copy(this.centre).setY(trunkHeight * 0.78);
    const ringScale = Math.max(0.001, c.innerRadius * structure);
    _scale.setScalar(ringScale);
    _rot.set(Math.PI / 2 + 0.28, this.age * c.flow * 0.24, this.seed);
    setConstructInstance(this.rings.mesh, 0, this.crownPosition, _scale, _rot);
    _rot.set(Math.PI / 2 - 0.36, -this.age * c.flow * 0.2, this.seed + Math.PI / 2);
    setConstructInstance(this.rings.mesh, 1, this.crownPosition, _scale, _rot);
    commitConstructInstances(this.rings.mesh, structure > 0.001 ? 2 : 0);
    syncConstruct(this.rings, c, {
      seed: this.seed + 2,
      reveal: bloom,
      fade,
      pulse: 0.5 + 0.5 * Math.sin(this.age * 3.1)
    });

    syncParticlePalette(this.leaves, c);
    let podCount = 0;
    let thornCount = 0;
    let hasFlyingPod = false;
    const podSize = Math.max(0.17, c.particleSize * 3.8) * fade;

    for (let i = 0; i < seedCount; i++) {
      const bit = 1 << i;
      const launchAt = bloomEnd + i * launchSpacing;
      const landAt = launchAt + flightDuration;
      const orbitAngle = this.seed + i * TAU / seedCount + time * c.flow;
      const launchAngle = this.seed + i * TAU / seedCount + launchAt * c.flow;
      const flight = saturate((time - launchAt) / flightDuration);

      this._ringPoint(
        _start,
        c.innerRadius * structure,
        launchAngle,
        Math.max(0.08, trunkHeight * 0.82 + Math.sin(launchAngle * 2 + i) * 0.16)
      );
      this._targetPoint(i, seedCount, _target);

      if (time >= launchAt && !(this.launchedMask & bit)) {
        this.launchedMask |= bit;
        this.lightBoost = Math.max(this.lightBoost, c.impactLight * 0.36);
        emitParticles(this.leaves, _emit, {
          position: _start,
          direction: _dir.copy(_target).sub(_start).normalize(),
          count: Math.max(10, c.particleRate * 0.14),
          radius: 0.14,
          speed: 4.2,
          spread: 0.3,
          size: c.particleSize,
          life: c.particleLife
        });
      }

      if (time < launchAt) {
        this._ringPoint(
          _pos,
          c.innerRadius * structure,
          orbitAngle,
          Math.max(0.08, trunkHeight * 0.82 + Math.sin(this.age * 3 + i) * 0.16)
        );
        _rot.set(this.age * 1.3 + i, orbitAngle, this.age * 0.9);
        setConstructInstance(this.pods.mesh, podCount++, _pos, podSize, _rot);
      } else if (flight < 1) {
        const travel = Easing.inOutCubic(flight);
        _pos.copy(_start).lerp(_target, travel);
        _pos.y += Math.sin(flight * Math.PI) * c.thornHeight * 1.35;
        _rot.set(this.age * 5 + i, orbitAngle + flight * 3.2, flight * 4.1);
        setConstructInstance(this.pods.mesh, podCount++, _pos, podSize * (1 - flight * 0.22), _rot);
        this.position.copy(_pos);
        hasFlyingPod = true;
      }

      if (time >= landAt && !(this.landedMask & bit)) {
        this.landedMask |= bit;
        this.lightBoost = Math.max(this.lightBoost, c.impactLight * 0.5);
        this.ctx.decals.spawn(DecalType.CRACK, _target, {
          radius: c.thornHeight * 0.72,
          life: Math.max(0, (c.lifetime - landAt) * settings.global.lifetime) + c.fadeTime,
          intensity: 0.48,
          colorA: getColor(c.colorB),
          colorB: getColor(c.colorC)
        });
        emitParticles(this.leaves, _emit, {
          position: _target,
          direction: _dir.set(0, 1, 0),
          count: Math.max(16, c.particleRate * 0.22),
          radius: c.thornHeight * 0.38,
          speed: 4.8,
          spread: 1,
          size: c.particleSize * 1.08,
          life: c.particleLife
        });
        this.ctx.shake.add(c.impactShake * 0.18 * settings.global.cameraShake, 0.2);
      }

      if (time >= landAt) {
        const growth = Easing.outBack(saturate((time - landAt) / safeTimelineDuration(0.25)));
        const targetAngle = this.seed + i * TAU / seedCount + (i % 2 ? 0.2 : -0.2);
        for (let thorn = 0; thorn < THORNS_PER_SEED; thorn++) {
          const thornAngle = targetAngle + (thorn - 1) * 0.3;
          const offset = (thorn - 1) * c.thornHeight * 0.16;
          _pos.copy(_target)
            .addScaledVector(this.side, Math.cos(thornAngle) * offset)
            .addScaledVector(this.direction, Math.sin(thornAngle) * offset);
          const height = c.thornHeight * (1 - thorn * 0.13) * growth * fade;
          _pos.y = Math.max(0.001, height * 0.5);
          _scale.set(
            Math.max(0.001, c.thornHeight * 0.105 * growth * fade),
            Math.max(0.001, height),
            Math.max(0.001, c.thornHeight * 0.105 * growth * fade)
          );
          _rot.set(
            Math.cos(thornAngle) * 0.22,
            -thornAngle,
            -Math.sin(thornAngle) * 0.22
          );
          setConstructInstance(this.thorns.mesh, thornCount++, _pos, _scale, _rot);
        }
      }
    }

    commitConstructInstances(this.pods.mesh, podCount);
    commitConstructInstances(this.thorns.mesh, thornCount);
    syncConstruct(this.pods, c, {
      seed: this.seed + 4,
      reveal: bloom,
      fade,
      pulse: 0.5 + 0.5 * Math.sin(this.age * 7.2)
    });
    syncConstruct(this.thorns, c, {
      seed: this.seed + 6,
      reveal: bloom,
      fade,
      pulse: 0.25 + 0.18 * Math.sin(this.age * 3.7)
    });

    if (!hasFlyingPod) this.position.copy(this.crownPosition);
    const ambientCount = this.emitter.tick(dt, c.particleRate * (0.35 + bloom * 0.48));
    if (ambientCount > 0) {
      emitParticles(this.leaves, _emit, {
        position: this.crownPosition,
        direction: _dir.set(0, 1, 0),
        count: ambientCount,
        radius: c.innerRadius * structure,
        speed: 1.45,
        spread: 0.95,
        size: c.particleSize,
        life: c.particleLife
      });
    }
  }

  onDestroy() {
    commitConstructInstances(this.branches.mesh, 0);
    commitConstructInstances(this.rings.mesh, 0);
    commitConstructInstances(this.pods.mesh, 0);
    commitConstructInstances(this.thorns.mesh, 0);
  }

  dispose() {
    disposeConstruct(this.branches);
    disposeConstruct(this.rings);
    disposeConstruct(this.pods);
    disposeConstruct(this.thorns);
    this.branchGeometry.dispose();
    this.ringGeometry.dispose();
    this.podGeometry.dispose();
    this.thornGeometry.dispose();
    super.dispose();
  }
}
